/**
 * Storage Page Logic
 *
 * Admin storage management page for pools, disks, and physical drives
 */

import { initAuthenticatedPage, hasPermission } from '../app/init.js';
import {
  fillPoolsTable,
  fillDisksTable,
  fillDrivesTable,
  createPool,
  cloneDisk,
  resizeDisk,
  deletePool,
  setStorageHost,
} from '../admin/storage.js';
import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { generateComboSelect } from '../ui/forms.js';
import { declareLoading, declareLoaded, notificationDialog } from '../ui/dialogs.js';

/**
 * Initialize the storage page
 */
export async function initStoragePage(): Promise<void> {
  const user = await initAuthenticatedPage();
  if (!user) return; // Will redirect to login

  // Check admin permission
  if (!hasPermission('admin')) {
    notificationDialog('Access denied: Admin permission required');
    window.location.href = '/';
    return;
  }

  // Load host dropdown (only show if multiple hosts exist)
  const hosts = await apiCall(api.getHostsForDropdown(), 'Failed to Load Hosts');
  if (hosts && Object.keys(hosts).length > 1) {
    const container = document.getElementById('storage-host-selector');
    if (container) {
      container.innerHTML = '<label>Host:&nbsp;</label>' + generateComboSelect('storage-host-select', '', hosts);
      container.style.display = '';
      $('#storage-host-select').on('change', function () {
        setStorageHost($(this).val() as string);
      });
    }
  }

  // Load all tables
  declareLoading();
  try {
    await Promise.all([fillPoolsTable(), fillDisksTable(), fillDrivesTable()]);
    // Show content after tables are ready
    const content = document.getElementById('content');
    if (content) content.style.display = 'block';
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Failed to load storage';
    notificationDialog(msg);
  }
  declareLoaded();
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).initStoragePage = initStoragePage;
  (window as unknown as Record<string, unknown>).createPool = createPool;
  (window as unknown as Record<string, unknown>).cloneDisk = cloneDisk;
  (window as unknown as Record<string, unknown>).resizeDisk = resizeDisk;
  (window as unknown as Record<string, unknown>).deletePool = deletePool;
}
